import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import Icon from "../../../components/Icon";
import Skeleton from "../../../components/Skeleton";
import { EmptyState, ErrorState } from "../../../components/State";
import { fetchAssetHealthSummary } from "../services/CMDBService";

const labelForCategory = (value) => {
  const text = String(value || "asset").toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const pct = (value) => `${Number(value || 0).toFixed(1)}%`;

function TooltipContent({ active, payload }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="asset-health-tooltip">
      <strong>{payload[0].payload.name}</strong>
      <span>{payload[0].value.toLocaleString()} assets</span>
    </div>
  );
}

export default function AssetHealthAnalysisView() {
  const [summary, setSummary] = useState(null);
  const [device, setDevice] = useState("all");
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState("24h");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (selectedRange = range) => {
      setLoading(true);
      setError(null);

      try {
        setSummary(await fetchAssetHealthSummary(selectedRange));
      } catch {
        setError("Could not load asset health analysis.");
      } finally {
        setLoading(false);
      }
    },
    [range],
  );

  useEffect(() => {
    load(range);
  }, [load, range]);

  const allCategories = useMemo(
    () => summary?.categories || [],
    [summary],
  );
  const categories = useMemo(
    () =>
      allCategories.filter(
        (item) => device === "all" || item.asset_category === device,
      ),
    [allCategories, device],
  );
  const visibleCategories = useMemo(
    () =>
      categories.map((item) => {
        if (status === "all") return item;

        const activeCount =
          status === "active" ? Number(item.active_count || 0) : 0;
        const inactiveCount =
          status === "inactive" ? Number(item.inactive_count || 0) : 0;

        return {
          ...item,
          asset_count: activeCount + inactiveCount,
          active_count: activeCount,
          inactive_count: inactiveCount,
        };
      }),
    [categories, status],
  );
  const totals = useMemo(() => {
    if (device === "all" && status === "all") return summary || {};

    const totalAssets = categories.reduce(
      (count, item) => count + Number(item.asset_count || 0),
      0,
    );
    const activeAssets =
      status === "inactive"
        ? 0
        : categories.reduce(
            (count, item) => count + Number(item.active_count || 0),
            0,
          );
    const inactiveAssets =
      status === "active"
        ? 0
        : categories.reduce(
            (count, item) => count + Number(item.inactive_count || 0),
            0,
          );

    return {
      total_assets: totalAssets,
      active_assets: activeAssets,
      inactive_assets: inactiveAssets,
      weighted_health: visibleCategories.reduce(
        (totalHealth, item) =>
          totalHealth +
          Number(item.average_health_percent || 0) *
            Number(item.asset_count || 0),
        0,
      ),
    };
  }, [summary, categories, visibleCategories, device, status]);

  const total = Number(totals.total_assets || 0);
  const active = Number(totals.active_assets || 0);
  const inactive = Number(
    totals.inactive_assets ?? Math.max(total - active, 0),
  );
  const overall =
    device === "all" && status === "all"
      ? Number(totals.overall_health_percent || 0)
      : total
        ? totals.weighted_health / total
        : 0;
  const donut = [
    { name: "Active assets", value: active, color: "#22C55E" },
    { name: "Inactive assets", value: inactive, color: "#EF4444" },
  ];

  if (error) {
    return (
      <div className="asset-health-analysis">
        <ErrorState message={error} onRetry={() => load(range)} />
      </div>
    );
  }
  if (loading) {
    return (
      <div className="asset-health-analysis">
        <Skeleton height={42} />
        <Skeleton height={420} style={{ marginTop: 16 }} />
      </div>
    );
  }
  if (!summary) {
    return (
      <div className="asset-health-analysis">
        <EmptyState
          icon="activity"
          title="No asset health data"
          subtitle="No asset health summary is available yet."
        />
      </div>
    );
  }

  return (
    <div className="asset-health-analysis">
      <header className="asset-health-analysis__header">
        <div>
          <h1>Asset Health Analysis</h1>
          <p>Consolidated infrastructure health across Servers and Routers</p>
        </div>
        <span>
          Last updated:{" "}
          {summary.calculated_at
            ? new Date(summary.calculated_at).toLocaleString()
            : "—"}
        </span>
      </header>

      <div className="asset-health-analysis__filters">
        <select
          value={device}
          onChange={(event) => setDevice(event.target.value)}
        >
          <option value="all">All Devices</option>
          {allCategories.map((item) => (
            <option key={item.asset_category} value={item.asset_category}>
              {labelForCategory(item.asset_category)}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={range} onChange={(event) => setRange(event.target.value)}>
          <option value="24h">24 Hours</option>
          <option value="7d">7 Days</option>
          <option value="30d">30 Days</option>
        </select>
        <button type="button" onClick={() => load(range)}>
          ↻&nbsp; Refresh
        </button>
      </div>

      <div className="asset-health-analysis__grid">
        <section className="asset-health-analysis__card asset-health-analysis__card--overall">
          <h2>Overall Infrastructure Health</h2>
          <p>Aggregated health across the complete infrastructure population</p>
          <div className="asset-health-analysis__overall-content">
            <div className="asset-health-analysis__donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donut}
                    dataKey="value"
                    innerRadius={86}
                    outerRadius={126}
                    startAngle={90}
                    endAngle={-270}
                    paddingAngle={1}
                  >
                    {donut.map((item) => (
                      <Cell key={item.name} fill={item.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<TooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="asset-health-analysis__donut-label">
                <strong>{pct(overall)}</strong>
                <span>● Healthy</span>
                {summary.health_change_percent !== null &&
                  summary.health_change_percent !== undefined && (
                    <small>
                      {summary.health_change_percent >= 0 ? "↑" : "↓"}{" "}
                      {Math.abs(summary.health_change_percent).toFixed(1)}% vs
                      last{" "}
                      {range === "24h"
                        ? "24 hours"
                        : range === "30d"
                          ? "30 days"
                          : "7 days"}
                    </small>
                  )}
              </div>
            </div>
            <div className="asset-health-analysis__stat-list">
              <div>
                <Icon name="layers" size={24} color="#1674E8" />
                <section>
                  <strong>{total.toLocaleString()}</strong>
                  <span>Total assets</span>
                </section>
              </div>
              <div>
                <Icon name="check" size={24} color="#16A34A" />
                <section>
                  <strong>{active.toLocaleString()}</strong>
                  <span>
                    Active assets · {pct(total ? (active * 100) / total : 0)}
                  </span>
                </section>
              </div>
              <div>
                <Icon name="x" size={24} color="#EF4444" />
                <section>
                  <strong>{inactive.toLocaleString()}</strong>
                  <span>
                    Inactive assets ·{" "}
                    {pct(total ? (inactive * 100) / total : 0)}
                  </span>
                </section>
              </div>
            </div>
          </div>
        </section>

        <section className="asset-health-analysis__card">
          <div className="asset-health-analysis__card-heading">
            <div>
              <h2>Infrastructure Health Trend</h2>
              <p>Collective health movement over time</p>
            </div>
            <div className="asset-health-analysis__range">
              {["24h", "7d", "30d"].map((value) => (
                <button
                  key={value}
                  className={range === value ? "active" : ""}
                  onClick={() => setRange(value)}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
          <div className="asset-health-analysis__trend-empty">
            Historical trend data will appear after health snapshots are
            collected.
          </div>
        </section>
      </div>

      <section className="asset-health-analysis__card asset-health-analysis__availability">
        <h2>Availability</h2>
        <p>Active versus inactive population by device type</p>
        {visibleCategories.map((item) => {
          const count = Number(item.asset_count || 0);
          const activePercent = count
            ? (Number(item.active_count || 0) * 100) / count
            : 0;

          return (
            <div
              className="asset-health-analysis__availability-row"
              key={item.asset_category}
            >
              <div>
                <strong>{labelForCategory(item.asset_category)}s</strong>
                <span>
                  {pct(activePercent)} active · {pct(100 - activePercent)}
                  {" "}inactive
                </span>
              </div>
              <div className="asset-health-analysis__bar">
                <i style={{ width: `${activePercent}%` }} />
                <b style={{ width: `${100 - activePercent}%` }} />
              </div>
            </div>
          );
        })}
        <div className="asset-health-analysis__legend">
          <span>
            <i /> Active
          </span>
          <span>
            <i /> Inactive
          </span>
        </div>
      </section>
    </div>
  );
}
